/* eslint-disable default-case */
import produce from 'immer';

import {
  types,
  SearchTransactionsRequest,
  SearchTransactionsSuccess,
  SearchTransactionsError,
  SearchTransactionsPending,
  DeliveryLogActionTypes,
  DeliveryLogState,
  GetTransactionPriceTypesPending,
  GetTransactionPriceTypesSuccess,
  GetTransactionPriceTypesRequest,
  RefundTransactionSuccess,
  VoidTransactionSuccess,
  ReconcileTransactionsRequest,
  ReconcileTransactionsError,
  GetSettlementReportSuccess,
  GetSettlementReportError,
  GetSettlementReportPending,
  GetSettlementReportRequest,
  GetSalesReportPending,
  GetSalesReportSuccess,
  GetSalesReportError,
  GetSalesReportRequest,
  GetSalesByCardTypeReportSuccess,
  GetSalesByCardTypeReportPending,
  GetSalesByCardTypeReportError,
  GetSalesByCardTypeReportRequest
} from './types';

// Actions
export const actions = {
  searchTransactions: (payload: SearchTransactionsRequest['payload']): DeliveryLogActionTypes => ({
    type: types.SEARCH_TRANSACTIONS.REQUEST,
    payload
  }),
  getTransactionPriceTypes: (payload: GetTransactionPriceTypesRequest['payload']): DeliveryLogActionTypes => ({
    type: types.GET_TRANSACTION_PRICE_TYPES.REQUEST,
    payload
  }),
  resetDeliveryLog: (): DeliveryLogActionTypes => ({ type: types.RESET_DELIVERY_LOG }),
  resetExportData: (): DeliveryLogActionTypes => ({ type: types.RESET_EXPORT_DATA }),
  refundTransaction: (payload: {
    transactionId: string;
    clientIntegrationId: string;
    email: string;
  }): DeliveryLogActionTypes => ({
    type: types.REFUND_TRANSACTION.REQUEST,
    payload
  }),
  voidTransaction: (payload: { transactionId: string; clientIntegrationId: string }): DeliveryLogActionTypes => ({
    type: types.VOID_TRANSACTION.REQUEST,
    payload
  }),
  reconcileTransactions: (payload: ReconcileTransactionsRequest['payload']): DeliveryLogActionTypes => ({
    type: types.RECONCILE_TRANSACTIONS.REQUEST,
    payload
  }),
  getSettlementReport: (payload: GetSettlementReportRequest['payload']): DeliveryLogActionTypes => ({
    type: types.GET_SETTLEMENT_REPORT.REQUEST,
    payload
  }),
  getSalesReport: (payload: GetSalesReportRequest['payload']): DeliveryLogActionTypes => ({
    type: types.GET_SALES_REPORT.REQUEST,
    payload
  }),
  getSalesByCardTypeReport: (payload: GetSalesByCardTypeReportRequest['payload']): DeliveryLogActionTypes => ({
    type: types.GET_SALES_BY_CARD_TYPE_REPORT.REQUEST,
    payload
  }),
  resetSalesByCardTypeReport: (): DeliveryLogActionTypes => ({
    type: types.RESET_SALES_BY_CARD_TYPE_REPORT
  }),
  resetSalesReport: (): DeliveryLogActionTypes => ({
    type: types.RESET_SALES_REPORT
  }),
  resetSettlementReport: (): DeliveryLogActionTypes => ({
    type: types.RESET_SETTLEMENT_REPORT
  })
};

// Initial State
export const initialState: DeliveryLogState = {
  data: [],
  hasMore: false,
  pending: false,
  error: null,
  totalRecords: 0,
  priceTypes: [],
  priceTypesPending: false,
  settlementReport: {
    data: null,
    total: null,
    pending: false,
    error: null
  },
  salesReport: {
    data: null,
    total: null,
    pending: false,
    error: null
  },
  salesByCardTypeReport: {
    data: null,
    pending: false,
    error: null
  },
  export: {
    data: [],
    pending: false
  }
};

// Reducer
export const reducer = (state: DeliveryLogState = initialState, action: DeliveryLogActionTypes): DeliveryLogState => {
  return produce(state, (draft) => {
    let payload;
    switch (action.type) {
      case types.SEARCH_TRANSACTIONS.PENDING:
        payload = (action as SearchTransactionsPending).payload;
        if (payload.exportData) {
          draft.export.pending = payload.pending;
        } else {
          draft.pending = payload.pending;
        }
        break;
      case types.SEARCH_TRANSACTIONS.SUCCESS:
        payload = (action as SearchTransactionsSuccess).payload;
        if (payload.exportData) {
          draft.export.data = payload.data;
        } else {
          draft.data = [...draft.data, ...payload.data];
          draft.hasMore = draft.data.length < payload.total;
          draft.totalRecords = payload.total;
        }
        break;
      case types.GET_TRANSACTION_PRICE_TYPES.PENDING:
        draft.priceTypesPending = (action as GetTransactionPriceTypesPending).payload;
        break;
      case types.REFUND_TRANSACTION.SUCCESS: {
        const updatedTx = (action as RefundTransactionSuccess).payload;
        draft.data = draft.data.map((tx) => (String(tx.id) === String(updatedTx.id) ? updatedTx : tx));
        break;
      }
      case types.VOID_TRANSACTION.SUCCESS: {
        const updatedTx = (action as VoidTransactionSuccess).payload;
        draft.data = draft.data.map((tx) => (String(tx.id) === String(updatedTx.id) ? updatedTx : tx));
        break;
      }
      case types.GET_TRANSACTION_PRICE_TYPES.SUCCESS:
        draft.priceTypes = (action as GetTransactionPriceTypesSuccess).payload;
        break;
      case types.GET_SETTLEMENT_REPORT.PENDING:
        draft.settlementReport.pending = (action as GetSettlementReportPending).payload;
        break;
      case types.GET_SETTLEMENT_REPORT.SUCCESS:
        draft.settlementReport.data = (action as GetSettlementReportSuccess).payload.data;
        draft.settlementReport.total = (action as GetSettlementReportSuccess).payload.total;
        break;
      case types.GET_SETTLEMENT_REPORT.ERROR:
        draft.settlementReport.error = (action as GetSettlementReportError).payload;
        break;
      case types.GET_SALES_REPORT.PENDING:
        draft.salesReport.pending = (action as GetSalesReportPending).payload;
        break;

      case types.GET_SALES_REPORT.SUCCESS:
        draft.salesReport.data = (action as GetSalesReportSuccess).payload.data;
        draft.salesReport.total = (action as GetSalesReportSuccess).payload.total;
        break;

      case types.GET_SALES_REPORT.ERROR:
        draft.salesReport.error = (action as GetSalesReportError).payload;
        break;
      case types.GET_SALES_BY_CARD_TYPE_REPORT.PENDING:
        draft.salesByCardTypeReport.pending = (action as GetSalesByCardTypeReportPending).payload;
        break;
      case types.GET_SALES_BY_CARD_TYPE_REPORT.SUCCESS:
        draft.salesByCardTypeReport.data = (action as GetSalesByCardTypeReportSuccess).payload.data;
        break;
      case types.GET_SALES_BY_CARD_TYPE_REPORT.ERROR:
        draft.salesByCardTypeReport.error = (action as GetSalesByCardTypeReportError).payload;
        break;
      case types.SEARCH_TRANSACTIONS.ERROR:
        draft.error = (action as SearchTransactionsError).payload;
        break;
      case types.RECONCILE_TRANSACTIONS.PENDING:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        draft.pending = (action as any).payload;
        break;
      case types.RECONCILE_TRANSACTIONS.ERROR:
        draft.error = (action as ReconcileTransactionsError).payload;
        break;
      case types.RESET_DELIVERY_LOG:
        draft.data = initialState.data;
        draft.hasMore = initialState.hasMore;
        draft.pending = initialState.pending;
        draft.error = initialState.error;
        draft.totalRecords = initialState.totalRecords;
        draft.export = initialState.export;
        break;
      case types.RESET_EXPORT_DATA:
        draft.export.data = initialState.export.data;
        break;
      case types.RESET_SALES_BY_CARD_TYPE_REPORT:
        draft.salesByCardTypeReport = initialState.salesByCardTypeReport;
        break;
      case types.RESET_SALES_REPORT:
        draft.salesReport = initialState.salesReport;
        break;
      case types.RESET_SETTLEMENT_REPORT:
        draft.settlementReport = initialState.settlementReport;
        break;
      default:
    }
  });
};
